import React, { useContext, useState, useEffect } from "react"
import { Link, useLocation } from "react-router-dom"
import { tryClaimAutoOpen } from "@components/tickets/ticketPurchaseDialogCoordinator"
import { useNavigateToPost } from "@hooks/useNavigateToPost"
import { EventDateFormat, EventTimeFormat } from "@util/dates"
import FreeTextFormat from "@components/shared/FreeTextFormat"
import CommentSectionPreview from "@components/comments/CommentSectionPreview"
import { isPast } from "date-fns"
import { UserContext } from "@components/HomeApp"
import { TextAsImage } from "./TextAsImage"
import { PostListItemStyles } from "./constants"
import InterestedBlock from "./InterestedBlock"
import ConcertLineup from "@components/shared/ConcertLineup"
import { ShareUrlButton } from "@components/ShareUrlButton"
import EventForm from "@components/EventForm"
import CreatedPostUserResponse from "./CreatedPostUserResponse"
import { ShowHideButton } from "@components/shared/Button"
import { ReactionBlock } from "./ReactionBlock"
import RelatedPosts from "@components/RelatedPosts"
import PremiereHeader from "./PremiereHeader"
import TicketPurchaseComponent from "@components/tickets/TicketPurchaseComponent"
import TicketPurchaseComponentLoggedOut from "@components/tickets/TicketPurchaseComponentLoggedOut"
import clsx from "clsx"
import styles from "./styles.module.css"
import GoingBlock from "./GoingBlock"
import { Concert } from "@models/events"

interface ConcertListItemProps {
  concert: Concert
  setPostListItem?: (concert: Concert) => void
  updateStatus?: () => void
  isExpanded?: boolean
  adminMode?: boolean
}

export const ConcertListItem: React.FC<ConcertListItemProps> = ({
  concert,
  setPostListItem = () => {},
  updateStatus = () => {},
  isExpanded,
  adminMode = false,
}) => {
  const [postStatus, setPostStatus] = useState(concert.status)
  const setConcert = (newConcert: Concert) => {
    setPostListItem(newConcert)
  }
  const [showEditForm, setShowEditForm] = useState(false)
  const { user } = useContext(UserContext)
  const location = useLocation()
  const isOwner = user && user.band && user.band.id === concert.band_id
  const acceptedCollaborators = (concert.post_collaborators ?? []).filter(
    (c) => c.status === "accepted",
  )
  const isPastEvent = concert.end_dt ? isPast(new Date(concert.end_dt)) : false
  const navigateToPostScreen = useNavigateToPost(concert.nada_path)

  const urlSaysAutoOpen =
    new URLSearchParams(location.search).get("auto-open-ticket-purchase") ===
    "true"

  const [shouldAutoOpenTicketPurchase, setShouldAutoOpenTicketPurchase] =
    useState(false)

  useEffect(() => {
    if (urlSaysAutoOpen) {
      const claimed = tryClaimAutoOpen()
      setShouldAutoOpenTicketPurchase(claimed)
    }
  }, [urlSaysAutoOpen, concert.id])

  const imgStyle: React.CSSProperties = {
    width: "100%",
    aspectRatio: "16 / 9",
    backgroundColor: "black",
  }

  return showEditForm ? (
    <EventForm
      setShowForm={setShowEditForm}
      concert={concert}
      setConcert={setConcert}
    />
  ) : (
    <div className="grid grid-cols-12">
      <CreatedPostUserResponse
        user_response={concert.user_response}
        type="concert"
        setShowEditForm={setShowEditForm}
      />
      <div className={PostListItemStyles.contentDiv}>
        <>
          {concert.cover_image_url_medium ? (
            <img
              src={concert.cover_image_url_medium}
              alt={concert.title}
              onClick={navigateToPostScreen}
              className="cursor-pointer w-full"
              style={imgStyle}
            />
          ) : concert.event_type !== "bNliveStream" &&
            concert.band_cover_photo_url_medium ? (
            <img
              src={concert.band_cover_photo_url_medium}
              alt={concert.band_name}
              onClick={navigateToPostScreen}
              className="cursor-pointer w-full"
            />
          ) : (
            <button
              className="cursor-pointer w-full block"
              onClick={navigateToPostScreen}
            >
              <TextAsImage
                text={
                  concert.status === "canceled" || concert.canceled_at
                    ? `Canceled! ${concert.title}`
                    : concert.status === "unpublished"
                      ? `${concert.title} (unlisted)`
                      : concert.title
                }
              />
            </button>
          )}
        </>
      </div>
      <div className={PostListItemStyles.metaDiv}>
        <PremiereHeader post={concert} size="small" />
        <div className="flex items-start justify-between">
          <div
            className={
              concert.status === "canceled" || concert.canceled_at
                ? "text-bn-neon-red"
                : ""
            }
          >
            <h2
              className={clsx(
                PostListItemStyles.header,
                "cursor-pointer",
                concert.is_bn_premiere && styles.premiereTitle,
              )}
              onClick={navigateToPostScreen}
            >
              {concert.status === "canceled" || concert.canceled_at ? (
                <>
                  Canceled!{" "}
                  <span
                    className="line-through"
                    dangerouslySetInnerHTML={{ __html: concert.title ?? "" }}
                  />
                </>
              ) : (
                <>
                  <span
                    dangerouslySetInnerHTML={{ __html: concert.title ?? "" }}
                  />
                  {concert.status === "unpublished" && (
                    <span className="text-xs font-normal text-gray-500">
                      {" "}
                      (unlisted)
                    </span>
                  )}
                </>
              )}
              <ShareUrlButton nadaUrl={concert.nada_url} renderMode="icon" />
            </h2>
            {(concert.status === "canceled" || concert.canceled_at) &&
            concert.canceled_event_message ? (
              <p className="text-sm mt-1 text-gray-700">
                {concert.canceled_event_message}
              </p>
            ) : null}
          </div>
        </div>

        <h6 className={PostListItemStyles.secondaryHeader}>
          {concert.status === "needs_approved" && isOwner ? (
            <span className="text-red-600 ml-4">
              Hidden Event{" "}
              <button
                className="text-blue-700 ml-2 border-b-2 border-blue-300 focus-outline-none"
                onClick={() => setShowEditForm(true)}
              >
                Publish for your fans now
              </button>
            </span>
          ) : null}
        </h6>

        <div>
          <div
            className={clsx(
              PostListItemStyles.postDate,
              (concert.status === "canceled" || concert.canceled_at) &&
                "line-through",
            )}
          >
            {EventDateFormat(concert.start_dt, concert.timezone)}~
            {EventTimeFormat(concert.end_dt, concert.timezone)}
            &nbsp;
            <a
              href={concert.add_to_calendar_url}
              target="_blank"
              rel="noreferrer"
              className="text-blue-700"
            >
              (+)
            </a>
          </div>

          {concert.event_type === "bNliveStream" ? (
            <div className="text-sm">
              <a href={concert.nada_url} className="text-gray-700">
                {concert.nada_url}
              </a>
            </div>
          ) : concert.bn_venue ? (
            <div className="text-sm">
              <Link
                to={concert.bn_venue.bandnada_path ?? ""}
                className="text-green-700 font-bold leading-none"
              >
                {concert.bn_venue.name}
              </Link>
            </div>
          ) : concert.venue_name || concert.venue_city_display_name ? (
            <div className="text-sm">
              <a
                className="text-gray-700"
                href={concert.google_map_url ?? undefined}
                target="_blank"
                rel="noreferrer"
              >
                {concert.venue_name && concert.venue_city_display_name
                  ? `${concert.venue_name} in ${concert.venue_city_display_name}`
                  : `${concert.venue_name}${concert.venue_city_display_name}`}
              </a>
            </div>
          ) : null}

          <div className={PostListItemStyles.postedBy}>
            Posted by{" "}
            <Link to={concert.local_path ?? ""}>{concert.band_name}</Link>
            {acceptedCollaborators.length > 0 && (
              <>
                {" w/ "}
                {acceptedCollaborators.map((collaborator, index) => (
                  <React.Fragment key={collaborator.id ?? collaborator.band_id}>
                    {index > 0 && ", "}
                    {collaborator.band_path ? (
                      <Link to={collaborator.band_path}>
                        {collaborator.band_name}
                      </Link>
                    ) : (
                      collaborator.band_name
                    )}
                  </React.Fragment>
                ))}
              </>
            )}
          </div>
          {concert.post_type === "concert" && (
            <div className={PostListItemStyles.externalLink}>
              <a href={concert.external_url ?? undefined} target="_blank" rel="noreferrer">
                view on&nbsp;
                {concert.class_name}
              </a>
            </div>
          )}
        </div>

        {concert.ticket_link_url ? (
          <div className="mt-2">
            <a
              rel="noreferrer"
              href={concert.ticket_link_url}
              target="_blank"
              className="border-b-2 border-blue-300 pt-1 text-blue-600 text-sm"
            >
              Ticket Link
            </a>
          </div>
        ) : null}

        <div className="mt-2">
          {concert.status === "canceled" || concert.canceled_at ? (
            concert.ticket_type === "bNTickets" &&
            concert.user_ticket_purchase_status === "cancelled_and_refunded" ? (
              <p className="text-sm text-gray-600">You have been refunded.</p>
            ) : null
          ) : concert.ticket_type === "bNTickets" && !isPastEvent ? (
            user ? (
              <TicketPurchaseComponent
                bandId={concert.band_id}
                bandName={concert.band_name}
                userName={user.name}
                userDefaultPaymentMethodLastFour={
                  user.default_payment_method_last_four
                }
                userDefaultPaymentMethodExpired={
                  user.default_payment_method_expired
                }
                postId={concert.id}
                ticketPrice={concert.ticket_price}
                maxTickets={concert.band_nada_ticket_max ?? 0}
                ticketsSold={concert.band_nada_tickets_sold ?? 0}
                eventEndDate={concert.end_dt}
                autoOpenDialog={shouldAutoOpenTicketPurchase}
                ticketPriceAtDoor={concert.ticket_price_at_door}
                concert={concert}
              />
            ) : (
              <TicketPurchaseComponentLoggedOut
                postId={concert.id}
                bandId={concert.band_id}
                bandName={concert.band_name}
                eventTitle={concert.title}
                ticketPrice={concert.ticket_price}
                maxTickets={concert.band_nada_ticket_max ?? 0}
                ticketsSold={concert.band_nada_tickets_sold ?? 0}
                eventEndDate={concert.end_dt}
                ticketPriceAtDoor={concert.ticket_price_at_door}
                concert={concert}
              />
            )
          ) : !isPastEvent ? (
            <div className="flex items-center">
              <div className="mr-2">
                <InterestedBlock
                  postId={concert.id}
                  renderMode="minimal"
                  maxRsvps={concert.max_rsvps}
                />
              </div>
              <GoingBlock postId={concert.id} renderMode="text" />
            </div>
          ) : null}
        </div>
        <ConcertLineup
          postId={concert.id}
          startTime={concert.start_dt}
          timezone={concert.timezone}
        />

        {concert.description && (
          <div className={PostListItemStyles.description}>
            <FreeTextFormat
              text={concert.description}
              expandable={false}
              expanded={false}
              linkify={true}
            />
          </div>
        )}

        <button
          className="focus:outline-none mt-2 text-blue-600 text-sm"
          onClick={navigateToPostScreen}
        >
          See all those details...
        </button>

        {adminMode && (
          <ShowHideButton
            updateStatus={updateStatus}
            postStatus={postStatus}
            setPostStatus={setPostStatus}
          />
        )}
        <div className="mt-4">
          <RelatedPosts postId={concert.id} />
        </div>
        <ReactionBlock
          contentType="Post"
          contentId={concert.id}
          postId={concert.id}
        />
        <CommentSectionPreview
          postId={concert.id}
          postPath={concert.nada_url}
        />
      </div>
    </div>
  )
}
